/**
 * Iter 67cy — SEO Landing page per location.
 *
 * Route:  /car-rental/:slug
 *
 * Each entry in the locations catalog (e.g. `tirana-airport-albania`) gets
 * its own crawlable HTML page with:
 *   • a unique <title> + meta description + OG tags (no react-helmet needed)
 *   • a clear H1 ("Car rental in Tirana Airport, Albania")
 *   • intro copy tailored to the location's `kind` (city / airport / port /
 *     tourist / hotel_area)
 *   • a list of available cars (filtered server-side by `pickup_location`)
 *   • JSON-LD structured data (Place + BreadcrumbList + ItemList) so
 *     Google can render rich results
 *   • internal links to sibling locations in the same country to keep
 *     PageRank flowing inside the platform
 *
 * The page is intentionally lightweight: it reuses `BrowseCarCard` so
 * design tokens stay consistent and the same forwarded query-string
 * logic (`pickup_location` etc.) flows into the car detail page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BrowseCarCard from "@/components/browse/BrowseCarCard";
import PopularRoutesWidget from "@/components/locations/PopularRoutesWidget";
import { Button } from "@/components/ui/button";
import { MapPin, Plane, Anchor, Building2, TreePalm, Search, Loader2, ArrowRight, ShieldCheck, Tag, CalendarClock, BadgeCheck } from "lucide-react";
import { getSlugOverride } from "@/lib/locationLandingOverrides";

const KIND_ICON = {
  airport: Plane,
  port: Anchor,
  city: Building2,
  tourist: TreePalm,
  hotel_area: Building2,
};

const KIND_TAGLINE = {
  airport:    "Pick up your rental car right at the terminal and hit the road minutes after landing.",
  port:       "Drive straight off the ferry and into your road trip — no transfers, no waiting.",
  city:       "Compare local hosts and international brands in one place, and book a car that's actually ready when you arrive.",
  tourist:    "Skip the tour-bus queues — rent a car on your terms and explore the area at your own pace.",
  hotel_area: "Have a car delivered to your hotel and reclaim your holiday from taxi apps.",
};

// Iter 67dw — city-specific keyword-rich intro paragraph used right below
// the H1 on every location landing page. We keep it generic but inject the
// location name + country so each of the ~180 generated pages is unique
// enough for Google to index separately.
function _seoIntro(loc) {
  const kind = (loc.kind || "city");
  const where =
    kind === "airport"
      ? `${loc.name} (${loc.country})`
      : kind === "port"
      ? `${loc.name} port`
      : loc.name;
  return `Looking to book a car in ${where}? RentalCar.al compares real offers from verified rent-a-car partners across ${loc.country} — clear prices, low or no deposit, no credit card required, and free cancellation up to 24h. Whether you need a budget hatchback for the city or a 4×4 for the coast, the right car rental at ${loc.name} is one search away.`;
}

// Iter 67dw — three benefit cards tailored to the location's kind. Each
// card title is keyword-anchored so location landing pages can rank for
// specific intents (e.g. "low deposit car rental Tirana Airport").
function _benefitCards(loc) {
  const place = loc.name;
  const base = [
    {
      icon: ShieldCheck,
      title: `Low or no-deposit car rental in ${place}`,
      text: "Add Full Protection at checkout to unlock low or no-deposit options on most cars — and skip the credit-card hold at the counter.",
      bg: "from-emerald-50 to-white",
      ring: "ring-emerald-200",
      fg: "text-emerald-700",
    },
    {
      icon: Tag,
      title: `Clear prices, no hidden fees`,
      text: `Taxes, mandatory fees, and required insurance for ${place} are shown upfront. The price you see is the price you pay.`,
      bg: "from-sky-50 to-white",
      ring: "ring-sky-200",
      fg: "text-sky-700",
    },
    {
      icon: CalendarClock,
      title: "Free cancellation up to 24h",
      text: `Plans changed? Most RentalCar.al reservations at ${place} can be modified or cancelled free of charge up to 24h before pickup.`,
      bg: "from-violet-50 to-white",
      ring: "ring-violet-200",
      fg: "text-violet-700",
    },
  ];
  return base;
}

function ensureMeta(selector, attr, value, content) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("content", content || "");
  return { el, created };
}

function ensureCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
    created = true;
  }
  el.setAttribute("href", href);
  return { el, created };
}

export default function LocationLandingPage() {
  const { slug } = useParams();
  const [loc, setLoc] = useState(null);
  const [cars, setCars] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // 1. Fetch the location row.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/locations/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => { if (!cancelled) setLoc(r.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // 2. Once the location is known, fetch matching cars + sibling slugs.
  useEffect(() => {
    if (!loc) return;
    let cancelled = false;
    api
      .get("/cars", { params: { pickup_location: loc.name } })
      .then((r) => { if (!cancelled) setCars(r.data || []); })
      .catch(() => { if (!cancelled) setCars([]); });
    api
      .get("/locations/landing-index", { params: { country: loc.country } })
      .then((r) => {
        if (cancelled) return;
        const others = (r.data || []).filter((row) => row.slug !== loc.slug);
        setSiblings(others.slice(0, 24));
      })
      .catch(() => { if (!cancelled) setSiblings([]); });
    return () => { cancelled = true; };
  }, [loc]);

  // 3. SEO <head> tags — title, description, OG, canonical, JSON-LD.
  useEffect(() => {
    if (!loc) return undefined;
    // Iter 67en+++++ — slug-specific overrides (currently the Tirana
    // Airport landing page, which needs IATA-code-rich copy and a
    // LocalBusiness/Airport JSON-LD graph).
    const override = getSlugOverride(loc.slug);

    const title =
      override?.seoTitle ||
      loc.seo_title ||
      `Car rental in ${loc.name}, ${loc.country} — low deposit, no credit card | RentalCar.al`;
    const desc =
      override?.seoDescription ||
      loc.seo_description ||
      `Book a car in ${loc.name}, ${loc.country} with RentalCar.al. Compare real offers from trusted local hosts — low or no deposit, no credit card required, free cancellation up to 24h. ${
        KIND_TAGLINE[loc.kind] || ""
      }`.trim();

    const prevTitle = document.title;
    document.title = title;

    const metas = [
      ensureMeta('meta[name="description"]', "name", "description", desc),
      ensureMeta('meta[property="og:title"]', "property", "og:title", title),
      ensureMeta('meta[property="og:description"]', "property", "og:description", desc),
      ensureMeta('meta[property="og:type"]', "property", "og:type", "website"),
      ensureMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary"),
    ];
    const canonical = ensureCanonical(`${window.location.origin}/car-rental/${loc.slug}`);

    // JSON-LD: Place + BreadcrumbList + slug-specific overrides.
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.dataset.dnLanding = loc.slug;
    const graph = [
      {
        "@type": "Place",
        name: `${loc.name}, ${loc.country}`,
        address: { "@type": "PostalAddress", addressCountry: loc.country, addressLocality: loc.name },
        url: `${window.location.origin}/car-rental/${loc.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
          { "@type": "ListItem", position: 2, name: loc.country, item: `${window.location.origin}/cars?country=${encodeURIComponent(loc.country)}` },
          { "@type": "ListItem", position: 3, name: loc.name },
        ],
      },
      // Iter 67en+++++ — slug-specific Airport + LocalBusiness fragments.
      ...((override?.schemaGraph?.() || [])),
    ];
    ldScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": graph,
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      metas.forEach(({ el, created }) => { if (created) el.remove(); });
      if (canonical.created) canonical.el.remove();
      ldScript.remove();
    };
  }, [loc]);

  const Icon = useMemo(() => KIND_ICON[loc?.kind] || MapPin, [loc]);
  // Iter 67en+++++ — slug-level override surfaces here so H1, subtitle
  // and benefit cards stay airport-aware.
  const override = useMemo(() => (loc ? getSlugOverride(loc.slug) : null), [loc]);
  const searchHref = useMemo(() => {
    if (!loc) return "/cars";
    const q = new URLSearchParams();
    q.set("pickup_location", loc.name);
    q.set("country", loc.country);
    return `/cars?${q.toString()}`;
  }, [loc]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="py-20 text-center text-neutral-500" data-testid="loc-landing-loading">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !loc) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="loc-landing-notfound">
          <h1 className="font-display text-3xl mb-3">Location not found</h1>
          <p className="text-neutral-500 mb-6">This destination may have been renamed or unpublished.</p>
          <Link to="/cars">
            <Button data-testid="loc-landing-browse-all-btn">Browse all cars</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`loc-landing-${loc.slug}`}>
      <Header />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-b border-border" data-testid="loc-landing-hero">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          {/* Breadcrumb */}
          <nav className="text-xs text-neutral-500 mb-4 flex items-center gap-1.5 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-neutral-900">Home</Link>
            <span>›</span>
            <Link to={`/cars?country=${encodeURIComponent(loc.country)}`} className="hover:text-neutral-900">
              {loc.country}
            </Link>
            <span>›</span>
            <span className="text-neutral-900 font-medium">{loc.name}</span>
          </nav>

          {/* Iter 67dw — brand-coloured trust ribbon above the H1. */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-neutral-900 text-amber-400 text-[10px] font-bold uppercase tracking-wider shadow-sm mb-4" data-testid="loc-landing-trust-ribbon">
            <BadgeCheck className="h-3 w-3" />
            No deposit · No credit card required
          </div>

          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-14 w-14 rounded-2xl bg-amber-400 text-neutral-900 items-center justify-center shadow-lg shadow-amber-500/30 ring-1 ring-amber-700/30 shrink-0">
              <Icon className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-neutral-900" data-testid="loc-landing-h1">
                {override?.h1 || (
                  <>Car rental in {loc.name}{loc.kind === "airport" ? "" : `, ${loc.country}`}</>
                )}
              </h1>
              <p className="mt-3 max-w-3xl text-base text-neutral-700 leading-relaxed" data-testid="loc-landing-seo-intro">
                {override?.subtitle || _seoIntro(loc)}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link to={searchHref}>
                  <Button size="lg" className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-amber-400 font-bold shadow-lg ring-1 ring-amber-500/30" data-testid="loc-landing-search-btn">
                    <Search className="h-4 w-4 mr-2" />
                    See available cars
                  </Button>
                </Link>
                <span className="text-xs uppercase tracking-wider text-neutral-500">
                  {cars.length} {cars.length === 1 ? "vehicle" : "vehicles"} available
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why book a car in <city> — Iter 67dw ─────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="loc-landing-benefits">
        <h2 className="font-display text-2xl text-neutral-900 mb-5">
          Why book a car in {loc.name} with <span className="text-amber-600">RentalCar.al</span>?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(override?.benefitCards || _benefitCards(loc)).map((b) => (
            <div
              key={b.title}
              className={`p-5 rounded-2xl border ${b.ring} bg-gradient-to-br ${b.bg}`}
              data-testid={`loc-landing-benefit-${b.title.replace(/\W+/g, "-").toLowerCase()}`}
            >
              <span className={`flex md:inline-flex h-20 w-20 md:h-10 md:w-10 rounded-2xl md:rounded-xl bg-white ring-1 ${b.ring} ${b.fg} items-center justify-center mb-3 mx-auto md:mx-0`}>
                <b.icon className="h-10 w-10 md:h-5 md:w-5" />
              </span>
              <div className="font-display text-base font-semibold text-neutral-900 mb-1 text-center md:text-left">{b.title}</div>
              <div className="text-[13px] text-neutral-600 leading-relaxed text-center md:text-left">{b.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Cars list ────────────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-2xl text-neutral-900">
            Cars available at {loc.name}
          </h2>
          {cars.length > 0 && (
            <Link
              to={searchHref}
              className="text-sm font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
              data-testid="loc-landing-view-all-link"
            >
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {cars.length === 0 ? (
          <div
            className="rounded-3xl border border-dashed border-border bg-neutral-50 p-10 text-center"
            data-testid="loc-landing-empty"
          >
            <MapPin className="h-8 w-8 mx-auto text-neutral-400 mb-3" />
            <h3 className="font-display text-xl mb-2">No cars listed in {loc.name} yet</h3>
            <p className="text-sm text-neutral-500 max-w-md mx-auto mb-5">
              Our hosts are expanding fast. While we build coverage here, you can browse nearby
              destinations in {loc.country}.
            </p>
            <Link to={`/cars?country=${encodeURIComponent(loc.country)}`}>
              <Button variant="outline" className="rounded-full" data-testid="loc-landing-empty-cta">
                Browse all cars in {loc.country}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cars.slice(0, 12).map((car, i) => (
              <BrowseCarCard key={car.id} car={car} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ── Why rent here? — kind-specific copy that doubles as SEO content. */}
      <section className="bg-neutral-50 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl text-neutral-900 mb-6">
            Why rent a car in {loc.name}?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="text-xs uppercase tracking-wider text-indigo-700 font-bold mb-2">Local hosts</div>
              <p className="text-sm text-neutral-600">
                Every car in {loc.name} is offered by a verified local host on RentalCar.al, with
                transparent vendor policies you can read before you book.
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="text-xs uppercase tracking-wider text-indigo-700 font-bold mb-2">No surprises at pickup</div>
              <p className="text-sm text-neutral-600">
                Insurance, deposits, mileage, fuel policy and add-ons are spelled out on every
                listing — so the price you see is the price you pay.
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-border p-5">
              <div className="text-xs uppercase tracking-wider text-indigo-700 font-bold mb-2">Flexible pickup</div>
              <p className="text-sm text-neutral-600">
                {loc.kind === "airport"
                  ? `Terminal pickup at ${loc.name} with meet-&-greet available from many hosts.`
                  : loc.kind === "port"
                  ? `Quayside delivery at ${loc.name} so you can drive off the ferry without delays.`
                  : `Pickup in ${loc.name}, plus optional delivery to hotels and nearby destinations.`}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Popular routes widget (Iter 67df) ─────────────────────────── */}
      <PopularRoutesWidget slug={loc.slug} pickupName={loc.name} />

      {/* ── Sibling locations — internal links ─────────────────────────── */}
      {siblings.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl text-neutral-900 mb-2">
            More car rental locations in {loc.country}
          </h2>
          <p className="text-sm text-neutral-500 mb-6">
            Browsing on a road trip? Hop between cities, airports and coastal towns in one click.
          </p>
          <div className="flex flex-wrap gap-2" data-testid="loc-landing-siblings">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/car-rental/${s.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border px-3.5 py-1.5 text-sm text-neutral-700 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
                data-testid={`loc-landing-sibling-${s.slug}`}
              >
                <MapPin className="h-3.5 w-3.5 text-neutral-400" />
                {s.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
